import { Alert, Button, FileInput, Group, Stack } from '@mantine/core';
import { IconDownload, IconFileSpreadsheet, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import lib from '../pms-library/PmsLibrary.module.css';
import { useDrillErrorText } from './DrillDrawers.js';
import type { DrillLibrarySource } from './source.js';
import type { DrillDefaultReport, DrillImportResult, DrillImportStatus } from './types.js';

const STATUS_TONE: Readonly<Record<DrillImportStatus, 'success' | 'info' | 'neutral'>> = {
  added: 'success',
  new_version: 'info',
  unchanged: 'neutral',
};

/**
 * Import / Export (ADR 0128 §7): Excel önizleme ve içe aktarma, dışa aktarma ve ürünün varsayılan
 * kütüphanesinin eksikleri. Önizleme hiçbir şey yazmaz; hatalı satır satır, alan ve kodla
 * gösterilir ve yazılmaz; hatalı satır varsa içe aktarma onay ister.
 */
export function DrillImportTab({
  source,
  canImport,
  canExport,
  onImported,
}: {
  source: DrillLibrarySource;
  canImport: boolean;
  canExport: boolean;
  onImported: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrillErrorText();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<DrillImportResult | null>(null);
  const [defaults, setDefaults] = useState<DrillDefaultReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drills.importer.${key}`, values);

  const guard = async (label: string, work: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await work();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(null);
    }
  };

  const run = (preview: boolean) =>
    guard(preview ? 'preview' : 'import', async () => {
      if (!source.importFile || !file) return;
      setConfirming(false);
      setResult(await source.importFile(file, preview));
      if (!preview) await onImported();
    });

  const doImport = () => {
    if ((result?.summary.skipped ?? 0) > 0 && !confirming) {
      setConfirming(true);
      return;
    }
    void run(false);
  };

  return (
    <div className={lib.ioGrid}>
      <Panel title={k('title')} icon={<IconFileSpreadsheet size={18} />}>
        <div className={lib.ioBody}>
          <p className={lib.ioText}>{k('description')}</p>
          <FileInput
            label={k('file')}
            placeholder={k('filePlaceholder')}
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            clearable
            value={file}
            onChange={(next) => {
              setFile(next);
              setResult(null);
              setConfirming(false);
            }}
            disabled={!canImport}
          />
          <Group>
            <Button
              variant="default"
              disabled={!canImport || file === null}
              loading={busy === 'preview'}
              onClick={() => void run(true)}
            >
              {t('drills.actions.preview')}
            </Button>
            <Button
              leftSection={<IconUpload size={16} aria-hidden />}
              disabled={!canImport || file === null || result === null}
              loading={busy === 'import'}
              onClick={doImport}
            >
              {t('drills.actions.import')}
            </Button>
          </Group>
          {error ? <Alert color="red">{error}</Alert> : null}
          {confirming && result ? (
            <Alert color="yellow">
              {k('confirmWithErrors', { count: result.summary.skipped })}
              <Group mt="sm">
                <Button size="xs" onClick={() => void run(false)}>
                  {t('drills.actions.import')}
                </Button>
                <Button size="xs" variant="default" onClick={() => setConfirming(false)}>
                  {t('drills.actions.cancel')}
                </Button>
              </Group>
            </Alert>
          ) : null}
          {result ? <ImportSummary result={result} /> : null}
        </div>
      </Panel>
      <div className={lib.ioSide}>
        <Panel title={k('defaultsTitle')}>
          <div className={lib.ioBody}>
            <p className={lib.ioText}>{k('defaultsText')}</p>
            <Group>
              <Button
                variant="default"
                disabled={!canImport || !source.loadDefaults}
                loading={busy === 'defaults'}
                onClick={() =>
                  void guard('defaults', async () => {
                    if (!source.loadDefaults) return;
                    setDefaults(await source.loadDefaults());
                    await onImported();
                  })
                }
              >
                {t('drills.actions.loadDefaults')}
              </Button>
            </Group>
            {defaults ? (
              <Alert color="green">
                {defaults.categories + defaults.subcategories + defaults.templates === 0
                  ? k('defaultsNone', { version: defaults.version })
                  : k('defaultsDone', defaults as unknown as Record<string, unknown>)}
              </Alert>
            ) : null}
          </div>
        </Panel>
        <Panel title={k('exportTitle')}>
          <div className={lib.ioBody}>
            <p className={lib.ioText}>{k('exportText')}</p>
            <Group>
              <Button
                variant="default"
                leftSection={<IconDownload size={16} aria-hidden />}
                disabled={!canExport || !source.exportFile}
                loading={busy === 'export'}
                onClick={() =>
                  void guard('export', async () => {
                    await source.exportFile?.();
                  })
                }
              >
                {t('drills.actions.export')}
              </Button>
            </Group>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function ImportSummary({ result }: { result: DrillImportResult }) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drills.importer.${key}`, values);
  const { summary } = result;
  return (
    <Stack gap="sm" role="region" aria-label={k('summary')}>
      <Alert color={result.preview ? 'blue' : 'green'}>
        {result.preview ? k('previewNote') : k('importedNote')}{' '}
        {k('counts', {
          added: summary.added,
          newVersion: summary.newVersion,
          unchanged: summary.unchanged,
          categories: summary.categoriesAdded,
        })}
        {summary.skipped > 0 ? ` ${k('skippedRows', { count: summary.skipped })}` : ''}
      </Alert>
      {result.rows.length > 0 ? (
        <div className={lib.issueList}>
          <table className={lib.summaryTable}>
            <thead>
              <tr>
                <th scope="col" data-numeric>
                  {k('row')}
                </th>
                <th scope="col">{t('drills.columns.code')}</th>
                <th scope="col">{t('drills.columns.name')}</th>
                <th scope="col">{t('drills.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row) => (
                <tr key={`${row.row}-${row.code}`}>
                  <td data-numeric>{row.row}</td>
                  <td>{row.code}</td>
                  <td>{row.name}</td>
                  <td>
                    <StatusBadge tone={STATUS_TONE[row.status]}>
                      {k(`statuses.${row.status}`)}
                    </StatusBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {result.errors.length > 0 ? (
        <div className={lib.issueList}>
          <table className={lib.summaryTable} aria-label={k('errors')}>
            <thead>
              <tr>
                <th scope="col" data-numeric>
                  {k('row')}
                </th>
                <th scope="col">{k('field')}</th>
                <th scope="col">{k('problem')}</th>
              </tr>
            </thead>
            <tbody>
              {result.errors.map((issue) => (
                <tr key={`${issue.row}-${issue.field}-${issue.code}`}>
                  <td data-numeric>{issue.row}</td>
                  <td>{issue.field}</td>
                  <td>{t(`drills.issues.${issue.code}`, { defaultValue: issue.code })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Stack>
  );
}
